import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { selectUserPreferences } from '../store/preferencesSlice';

interface ThemeContextType {
  isDark: boolean;
  fontSize: string;
  compactMode: boolean;
  highContrast: boolean;
  applyTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const preferences = useSelector(selectUserPreferences);
  const [isDark, setIsDark] = useState(false);

  // Apply theme based on preferences
  const applyTheme = () => {
    const root = document.documentElement;

    // Remove existing theme classes first
    root.classList.remove('dark');

    // Theme application
    if (preferences.theme === 'dark') {
      setIsDark(true);
      root.classList.add('dark');
      console.log('Applied dark theme');
    } else if (preferences.theme === 'light') {
      setIsDark(false);
      // dark class already removed
      console.log('Applied light theme');
    } else if (preferences.theme === 'system') {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(prefersDark);
      if (prefersDark) {
        root.classList.add('dark');
        console.log('Applied system dark theme');
      } else {
        console.log('Applied system light theme');
      }
    }

    // Font size application
    root.classList.remove('text-sm', 'text-base', 'text-lg');
    if (preferences.fontSize === 'small') {
      root.classList.add('text-sm');
    } else if (preferences.fontSize === 'large') {
      root.classList.add('text-lg');
    } else {
      root.classList.add('text-base');
    }

    // Compact mode
    if (preferences.compactMode) {
      root.classList.add('compact-mode');
    } else {
      root.classList.remove('compact-mode');
    }

    // High contrast
    if (preferences.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  };

  // Apply theme when preferences change
  useEffect(() => {
    applyTheme();
  }, [preferences]);

  // Listen for system theme changes when using system preference
  useEffect(() => {
    if (preferences.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme();

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [preferences.theme]);

  const value: ThemeContextType = {
    isDark,
    fontSize: preferences.fontSize,
    compactMode: preferences.compactMode,
    highContrast: preferences.highContrast,
    applyTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// CSS custom properties for theme variables
const themeStyles = `
  :root {
    --bg-primary: #ffffff;
    --bg-secondary: #f8fafc;
    --text-primary: #1e293b;
    --text-secondary: #64748b;
    --border-color: #e2e8f0;
    --shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .dark {
    --bg-primary: #0f172a;
    --bg-secondary: #1e293b;
    --text-primary: #f1f5f9;
    --text-secondary: #cbd5e1;
    --border-color: #334155;
    --shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3);
  }

  .high-contrast {
    --bg-primary: #000000;
    --bg-secondary: #1a1a1a;
    --text-primary: #ffffff;
    --text-secondary: #cccccc;
    --border-color: #ffffff;
  }

  .compact-mode .p-4 {
    padding: 0.75rem !important;
  }

  .compact-mode .mb-4 {
    margin-bottom: 1rem !important;
  }

  .compact-mode .py-3 {
    padding-top: 0.75rem !important;
    padding-bottom: 0.75rem !important;
  }
`;

// Inject styles into head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = themeStyles;
  document.head.appendChild(styleSheet);
}
